import { useCallback, useEffect, useState } from 'react';
import { CircleCheck, CircleAlert, Copy, ExternalLink } from 'lucide-react';
import { api } from '../api.js';
import { Sheet, Field, Btn, Spinner } from '../ui/kit.js';

/* Adding a Claude account, from the phone (V8 spec §8).
 *
 * This flow has never once completed — V3 died on `Missing state parameter`
 * and V4 hung at "Verifying" for hours with nothing to read. So the screen is
 * built around the two things that were missing: every step says what state it
 * is in, and the CLI's own output is on screen the whole time, so a stall is
 * something you can look at rather than something you have to guess at.
 *
 * The URL is passed through exactly as the CLI emitted it. It is never
 * rebuilt, never re-encoded and never opened by the daemon: it is a link you
 * press, and the code you are shown afterwards comes back through the box.
 */

export interface AddSession {
  id: string;
  accountId: string;
  label: string;
  state: 'starting' | 'awaiting_code' | 'verifying' | 'done' | 'failed';
  url: string | null;
  error: string | null;
  tail: string;
  startedAt: string;
  verified: { fingerprint: string | null; duplicateOf: string | null } | null;
}

const STEP_WORDS: Record<AddSession['state'], string> = {
  starting: 'Asking the CLI for a sign-in link…',
  awaiting_code: 'Sign in, then paste the code it gives you.',
  verifying: 'Checking the token against the API…',
  done: 'The account is added and working.',
  failed: 'It did not finish.',
};

export function AddAccountSheet(props: { onClose: () => void; onAdded: () => void }) {
  const [accountId, setAccountId] = useState('');
  const [label, setLabel] = useState('');
  const [session, setSession] = useState<AddSession | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const poll = useCallback(async (id: string) => {
    try {
      const r = await api.get<{ session: AddSession }>(`/api/accounts/add/${id}`);
      setSession(r.session);
      if (r.session.state === 'done') props.onAdded();
    } catch {
      // the retry in api.ts has already tried; the next tick tries again
    }
  }, [props]);

  useEffect(() => {
    if (!session || session.state === 'done' || session.state === 'failed') return;
    const t = setInterval(() => void poll(session.id), 2000);
    return () => clearInterval(t);
  }, [session, poll]);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ session: AddSession }>('/api/accounts/add', {
        accountId: accountId.trim().toLowerCase(),
        label: label.trim() || accountId.trim(),
      });
      setSession(r.session);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const sendCode = async () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ session: AddSession }>(`/api/accounts/add/${session.id}/code`, { code });
      setSession(r.session);
      setCode('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title="Add a Claude account" onClose={props.onClose}>
      {!session ? (
        <>
          <p className="ops-add__intro">
            The box runs <span className="ops-sha">claude setup-token</span> and shows you its sign-in link. You sign in on
            your phone, and the code it gives you comes back here. The token is stored on the box and proved against the
            API before it is kept.
          </p>
          <Field label="Short id" hint="letters, digits and underscores — this is what the logs call it">
            <input
              className="ops-input"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="doccla"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              data-testid="add-account-id"
            />
          </Field>
          <Field label="Label" hint="the address, so a person can tell them apart">
            <input
              className="ops-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="niklas.inderst@doccla.com"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              data-testid="add-account-label"
            />
          </Field>
          {error ? (
            <p className="ops-add__err">
              <CircleAlert size={15} /> {error}
            </p>
          ) : null}
          <div className="ops-add__actions">
            <Btn kind="primary" big disabled={busy || !accountId.trim()} onClick={() => void start()} data-testid="add-account-start">
              {busy ? <Spinner /> : null} Start
            </Btn>
          </div>
        </>
      ) : (
        <>
          <ol className="ops-add__steps">
            <Step n={1} done={Boolean(session.url)} active={session.state === 'starting'} label="A sign-in link from the CLI" />
            <Step n={2} done={session.state === 'verifying' || session.state === 'done'} active={session.state === 'awaiting_code'} label="You sign in and paste the code" />
            <Step n={3} done={session.state === 'done'} active={session.state === 'verifying'} label="The token is proved against the API" />
          </ol>

          <p className={`ops-add__state ops-add__state--${session.state}`} data-testid="add-account-state">
            {session.state === 'done' ? <CircleCheck size={15} /> : session.state === 'failed' ? <CircleAlert size={15} /> : <Spinner />}
            {STEP_WORDS[session.state]}
          </p>

          {session.url && session.state === 'awaiting_code' ? (
            <div className="ops-add__link">
              <a href={session.url} target="_blank" rel="noopener noreferrer" className="ops-add__open" data-testid="add-account-link">
                <ExternalLink size={16} /> Open the sign-in page
              </a>
              <button
                type="button"
                className="ops-add__copy"
                onClick={() => {
                  void navigator.clipboard?.writeText(session.url!).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  });
                }}
              >
                <Copy size={14} /> {copied ? 'Copied' : 'Copy the link'}
              </button>
              <code className="ops-add__url">{session.url}</code>
            </div>
          ) : null}

          {session.state === 'awaiting_code' ? (
            <>
              <Field label="The code from the sign-in page" hint="paste it exactly as it was given to you">
                <input
                  className="ops-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="paste here"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-testid="add-account-code"
                />
              </Field>
              <div className="ops-add__actions">
                <Btn kind="primary" big disabled={busy || !code.trim()} onClick={() => void sendCode()} data-testid="add-account-send">
                  {busy ? <Spinner /> : null} Send the code
                </Btn>
              </div>
            </>
          ) : null}

          {session.state === 'done' && session.verified ? (
            <div className="ops-add__ok" data-testid="add-account-done">
              <p>
                <strong>{session.label}</strong> is added as <span className="ops-sha">{session.accountId}</span> and a live
                call proved the token reaches the API.
              </p>
              {session.verified.duplicateOf ? (
                <p className="ops-add__dup">
                  <CircleAlert size={15} /> Careful: its limit windows are identical to <strong>{session.verified.duplicateOf}</strong>,
                  which means this token was minted from that same account rather than a new one. Two labels, one account.
                </p>
              ) : (
                <p className="ops-add__distinct">Its limit windows differ from every other account, so it really is a separate account.</p>
              )}
            </div>
          ) : null}

          {session.error ? (
            <p className="ops-add__err">
              <CircleAlert size={15} /> {session.error}
            </p>
          ) : null}
          {error ? (
            <p className="ops-add__err">
              <CircleAlert size={15} /> {error}
            </p>
          ) : null}

          {session.tail ? (
            <details className="ops-add__tail">
              <summary>What the CLI is printing</summary>
              <pre>{session.tail}</pre>
            </details>
          ) : null}

          <div className="ops-add__actions">
            <Btn kind="ghost" onClick={props.onClose}>
              {session.state === 'done' ? 'Close' : 'Stop and close'}
            </Btn>
          </div>
        </>
      )}
    </Sheet>
  );
}

function Step(props: { n: number; label: string; done: boolean; active: boolean }) {
  return (
    <li className={`ops-add__step${props.done ? ' ops-add__step--done' : ''}${props.active ? ' ops-add__step--active' : ''}`}>
      <span className="ops-add__stepn">{props.done ? <CircleCheck size={14} /> : props.n}</span>
      <span>{props.label}</span>
    </li>
  );
}
